import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Article,
  BookOpen,
  VideoCamera,
  EnvelopeSimple,
  Rocket,
  Sparkle,
  UserPlus,
  Question,
  List as ListIcon,
  X as XIcon,
  CaretDown,
  ArrowRight,
} from "@phosphor-icons/react";
import ChatbotWidget from "./ChatbotWidget";

// ────────────────────────────────────────────────────────────────────────────
// Iter207 — Ristrutturazione navbar pubblica:
//   • Rimossi: Home, Contatti
//   • Rinominato: Mappa → Chi siamo (nuova pagina /chi-siamo con mappa embed)
//   • Aggiunto: Diventa Partner → /register (nessuna nuova pagina)
//   • Aggiunto: Risorse (mega-menu 3 colonne + card viola). Su mobile diventa
//     un accordion dentro l'hamburger.
// ────────────────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { to: "/chi-siamo", label: "Chi siamo", testid: "public-nav-chi-siamo" },
  { to: "/register", label: "Diventa Partner", testid: "public-nav-partner" },
];

const RESOURCE_COLUMNS = [
  {
    heading: "Approfondisci",
    items: [
      { icon: Article, label: "Blog", to: "/blog" },
      { icon: BookOpen, label: "Guide", to: "/blog" },
      { icon: VideoCamera, label: "Webinar", to: "/blog" },
      { icon: EnvelopeSimple, label: "Newsletter", to: "/#contatti" },
    ],
  },
  {
    heading: "Strumenti gratuiti",
    items: [
      { icon: Rocket, label: "Esplora demo", to: "/#demo", demo: true },
      { icon: Sparkle, label: "Crea documenti con AI", to: "/register" },
    ],
  },
  {
    heading: "Passa a Housetag",
    items: [
      { icon: UserPlus, label: "Registrati gratis", to: "/register" },
      { icon: Question, label: "Help center", to: "/contatti" },
    ],
  },
];

// Lancia demo-login (chiamato dal link "Esplora demo" nel mega-menu)
import { startDemo } from "@/lib/demo";

function ResourceLink({ item, onClick }) {
  const Ico = item.icon;
  const handleClick = (e) => {
    if (item.demo) {
      e.preventDefault();
      startDemo();
    }
    onClick?.();
  };
  return (
    <Link
      to={item.to}
      onClick={handleClick}
      className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-md text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
      data-testid={`resources-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className="w-8 h-8 rounded-md bg-purple-50 flex items-center justify-center shrink-0">
        <Ico size={16} weight="duotone" color="#6D28D9" />
      </span>
      <span className="font-medium">{item.label}</span>
    </Link>
  );
}

function ResourcesMegaMenu({ onNavigate, latestPosts = [] }) {
  return (
    <div
      className="grid grid-cols-4 gap-6 p-6"
      data-testid="resources-megamenu"
    >
      {RESOURCE_COLUMNS.map((col) => (
        <div key={col.heading}>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-3">
            {col.heading}
          </div>
          <div className="space-y-0.5">
            {col.items.map((it) => (
              <ResourceLink key={it.label} item={it} onClick={onNavigate} />
            ))}
          </div>
        </div>
      ))}
      {/* Iter207.5 — Card viola con preview dei 3 articoli più recenti */}
      <div
        className="rounded-xl p-4 ht-gradient text-white flex flex-col"
        data-testid="resources-promo-card"
      >
        <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold mb-3">
          Dal blog
        </div>
        {latestPosts.length === 0 ? (
          <div className="flex-1 flex flex-col justify-between">
            <p className="text-sm text-white/85 leading-relaxed">
              Presto qui vedrai gli articoli più recenti dal nostro blog.
            </p>
            <Link
              to="/blog"
              onClick={onNavigate}
              className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white text-purple-700 text-xs font-semibold self-start hover:bg-purple-50 transition-colors"
              data-testid="resources-promo-cta"
            >
              Vai al blog →
            </Link>
          </div>
        ) : (
          <div className="flex-1 flex flex-col justify-between">
            <div className="space-y-2.5">
              {latestPosts.slice(0, 3).map((p) => (
                <Link
                  key={p._id}
                  to={`/blog/${p.slug}`}
                  onClick={onNavigate}
                  className="block group"
                  data-testid={`resources-promo-post-${p.slug}`}
                >
                  <div className="text-[10px] text-white/60 uppercase tracking-wider">
                    {new Date(p.created_at).toLocaleDateString("it-IT", {
                      day: "numeric",
                      month: "short",
                    })}
                  </div>
                  <div className="text-sm font-semibold leading-snug line-clamp-2 group-hover:underline">
                    {p.title}
                  </div>
                </Link>
              ))}
            </div>
            <Link
              to="/blog"
              onClick={onNavigate}
              className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-white/90 hover:text-white self-start"
              data-testid="resources-promo-cta"
            >
              Tutti gli articoli <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PublicLayout() {
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resOpen, setResOpen] = useState(false); // desktop hover state
  const [mobileResOpen, setMobileResOpen] = useState(false); // mobile accordion
  const [latestPosts, setLatestPosts] = useState([]);
  const hoverTimer = useRef(null);

  // Iter207.5 — Fetch dei 3 articoli più recenti per il preview nel mega-menu
  useEffect(() => {
    let cancelled = false;
    api.get("/public/blog").then((r) => {
      if (!cancelled) setLatestPosts((r.data || []).slice(0, 3));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Chiudi mobile menu quando cambia route
  useEffect(() => {
    setMobileOpen(false);
    setMobileResOpen(false);
    setResOpen(false);
  }, [loc.pathname]);

  const openRes = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setResOpen(true);
  };
  const scheduleCloseRes = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setResOpen(false), 150);
  };

  const linkClass = (active) =>
    `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
      active
        ? "text-purple-700"
        : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur bg-white/85 border-b">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          {/* Logo → homepage (o dashboard se loggato) */}
          <Link
            to={localStorage.getItem("ht_token") ? "/dashboard" : "/"}
            className="flex items-center gap-2"
            data-testid="public-brand"
          >
            <img
              src="/logo.png"
              alt="Housetag"
              className="w-9 h-9 object-contain"
            />
            <div className="font-display text-xl font-bold tracking-tight">
              <span className="ht-gradient-text">Housetag.it</span>
            </div>
          </Link>

          {/* Nav desktop */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => linkClass(isActive)}
                data-testid={n.testid}
              >
                {n.label}
              </NavLink>
            ))}
            {/* Voce Risorse con mega-menu */}
            <div
              className="relative"
              onMouseEnter={openRes}
              onMouseLeave={scheduleCloseRes}
            >
              <button
                type="button"
                className={`${linkClass(false)} inline-flex items-center gap-1`}
                data-testid="public-nav-risorse"
                aria-expanded={resOpen}
                aria-haspopup="true"
              >
                Risorse{" "}
                <CaretDown
                  size={12}
                  weight="bold"
                  className={`transition-transform ${resOpen ? "rotate-180" : ""}`}
                />
              </button>
              {resOpen && (
                <div
                  className="absolute right-0 top-full pt-3 max-w-[100vw] overflow-hidden"
                  onMouseEnter={openRes}
                  onMouseLeave={scheduleCloseRes}
                >
                  <div
                    className="w-[880px] max-w-[calc(100vw-2rem)] rounded-xl border bg-white shadow-2xl overflow-hidden"
                    data-testid="resources-dropdown-desktop"
                  >
                    <ResourcesMegaMenu
                      onNavigate={() => setResOpen(false)}
                      latestPosts={latestPosts}
                    />
                  </div>
                </div>
              )}
            </div>
            {/* Blog rimane visibile come voce diretta */}
            <NavLink
              to="/blog"
              className={({ isActive }) => linkClass(isActive)}
              data-testid="public-nav-blog"
            >
              Blog
            </NavLink>
          </nav>

          <div className="hidden md:flex gap-2">
            <Link
              to="/login"
              className="px-4 py-2 text-sm font-medium hover:text-purple-700"
              data-testid="public-nav-login"
            >
              Accedi
            </Link>
            <Link
              to="/register"
              className="px-4 py-2 text-sm font-medium rounded-md ht-gradient border-0 text-white"
              data-testid="public-nav-register"
            >
              Registrati
            </Link>
          </div>

          {/* Hamburger mobile */}
          <button
            type="button"
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md border border-slate-200"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="public-nav-hamburger"
            aria-label="Apri menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? (
              <XIcon size={20} weight="bold" />
            ) : (
              <ListIcon size={20} weight="bold" />
            )}
          </button>
        </div>

        {/* Menu mobile */}
        {mobileOpen && (
          <div
            className="md:hidden border-t bg-white"
            data-testid="public-nav-mobile"
          >
            <div className="px-6 py-4 space-y-1">
              {NAV_ITEMS.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `block py-2.5 px-3 rounded-md text-base font-medium ${
                      isActive
                        ? "bg-purple-50 text-purple-700"
                        : "text-slate-700"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              {/* Risorse mobile accordion */}
              <button
                type="button"
                onClick={() => setMobileResOpen((v) => !v)}
                className="w-full flex items-center justify-between py-2.5 px-3 rounded-md text-base font-medium text-slate-700"
                data-testid="public-nav-risorse-mobile"
                aria-expanded={mobileResOpen}
              >
                <span>Risorse</span>
                <CaretDown
                  size={14}
                  weight="bold"
                  className={`transition-transform ${mobileResOpen ? "rotate-180" : ""}`}
                />
              </button>
              {mobileResOpen && (
                <div
                  className="pl-3 pb-2 space-y-0.5"
                  data-testid="resources-dropdown-mobile"
                >
                  {/* Iter207.4 — Su mobile la voce Risorse è un semplice
                      accordion con lista verticale piatta: no colonne,
                      no heading, no card promo. */}
                  {RESOURCE_COLUMNS.flatMap((col) => col.items).map((it) => (
                    <ResourceLink
                      key={it.label}
                      item={it}
                      onClick={() => setMobileOpen(false)}
                    />
                  ))}
                </div>
              )}
              <NavLink
                to="/blog"
                className={({ isActive }) =>
                  `block py-2.5 px-3 rounded-md text-base font-medium ${
                    isActive
                      ? "bg-purple-50 text-purple-700"
                      : "text-slate-700"
                  }`
                }
              >
                Blog
              </NavLink>
              <div className="border-t pt-3 mt-3 flex flex-col gap-2">
                <Link
                  to="/login"
                  className="w-full text-center py-2.5 rounded-md border text-slate-700 font-medium"
                >
                  Accedi
                </Link>
                <Link
                  to="/register"
                  className="w-full text-center py-2.5 rounded-md ht-gradient text-white font-semibold"
                >
                  Registrati
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="mt-16 border-t bg-[color:hsl(var(--surface-alt))]">
        <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Colonna 1 — Logo + tagline */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src="/logo.png" alt="Housetag" className="w-8 h-8" />
              <div className="font-display font-bold ht-gradient-text">
                Housetag.it
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              La gestione dei tuoi immobili, semplice e digitale.
            </p>
          </div>

          {/* Colonna 2 — Naviga */}
          <div>
            <div className="label-eyebrow mb-2">Naviga</div>
            <ul className="space-y-1 text-sm">
              <li>
                <Link
                  to="/chi-siamo"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="footer-nav-chi-siamo"
                >
                  Chi siamo
                </Link>
              </li>
              <li>
                <Link
                  to="/register"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="footer-nav-partner"
                >
                  Diventa Partner
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="footer-nav-risorse"
                >
                  Risorse
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="footer-nav-blog"
                >
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Colonna 3 — Risorse (Iter207.6) */}
          <div>
            <div className="label-eyebrow mb-2">Risorse</div>
            <ul className="space-y-1 text-sm" data-testid="footer-risorse-list">
              <li>
                <button
                  type="button"
                  onClick={startDemo}
                  className="text-muted-foreground hover:text-foreground text-left"
                  data-testid="footer-risorse-demo"
                >
                  Esplora demo
                </button>
              </li>
              <li>
                <Link
                  to="/register"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="footer-risorse-ai"
                >
                  Crea documenti con AI
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="footer-risorse-guide"
                >
                  Guide
                </Link>
              </li>
              <li>
                <Link
                  to="/blog"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="footer-risorse-webinar"
                >
                  Webinar
                </Link>
              </li>
              <li>
                <Link
                  to="/#contatti"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="footer-risorse-newsletter"
                >
                  Newsletter
                </Link>
              </li>
              <li>
                <Link
                  to="/contatti"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="footer-risorse-help"
                >
                  Help center
                </Link>
              </li>
            </ul>
          </div>

          {/* Colonna 4 — Legale */}
          <div>
            <div className="label-eyebrow mb-2">Legale</div>
            <ul className="space-y-1 text-sm mb-3">
              <li>
                <Link
                  to="/privacy"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="footer-privacy"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="footer-terms"
                >
                  Termini di Servizio
                </Link>
              </li>
            </ul>
            <p className="text-xs text-muted-foreground" data-testid="footer-copyright">
              © {new Date().getFullYear()} Housetag.it — Tutti i diritti riservati
            </p>
          </div>
        </div>
      </footer>
      <ChatbotWidget />
    </div>
  );
}

